import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UserPlus, X, ShieldCheck } from "lucide-react";

export default function Consultants() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [dirSchools, setDirSchools] = useState([]);
  const [pick, setPick] = useState({});

  const load = useCallback(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/directory/schools").then((r) => setDirSchools(r.data.filter((s) => s.active))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleConsultant = async (u) => {
    try {
      await api.patch(`/admin/consultants/${u.id}`, { is_consultant: !u.is_consultant });
      load();
      toast.success(u.is_consultant
        ? `${u.name} is no longer consultant-eligible.`
        : `${u.name} can now be assigned to schools as a consultant.`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const assign = async (u) => {
    const dirId = pick[u.id];
    if (!dirId) return;
    try {
      await api.post("/directory/assignments", { consultant_id: u.id, directory_school_id: dirId });
      setPick({ ...pick, [u.id]: "" });
      load();
      toast.success("School assigned. Its directory contacts are now available to this consultant.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const endAssignment = async (a) => {
    if (!window.confirm("Remove this school assignment? The consultant immediately loses access to the school's contacts. Historical records are preserved.")) return;
    try { await api.post(`/directory/assignments/${a.id}/end`); load(); toast.success("Assignment removed."); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (users === null) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="consultants-loading">Loading team…</div>;
  const schoolName = (dirId) => dirSchools.find((s) => s.dir_id === dirId)?.name || dirId;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="consultants-page">
      <div>
        <h1 className="font-serif font-bold text-2xl text-navy">Consultants</h1>
        <p className="text-sm text-[#57534E]">
          Manage who works as a consultant and which CKingEducation directory schools they're assigned to.
          Consultant eligibility (is_consultant) is separate from system role — an Admin can also be a consultant.
          Only schools synced from the CKingEducation directory can be assigned; assignments are stored in Operations and never written back to the website.
        </p>
      </div>
      <div className="space-y-4">
        {users.map((u) => {
          const assignable = u.role === "consultant" || u.is_consultant;
          const dirAssigns = (u.assignments || []).filter((a) => a.directory_school_id);
          return (
            <div key={u.id} className="card p-5 space-y-3" data-testid={`consultant-card-${u.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-bold text-navy">{u.name}</span>
                <span className="text-xs text-[#78716C]">{u.email}</span>
                <span className={`${u.role === "admin" ? "badge-approved" : "badge-neutral"}`} data-testid={`consultant-role-${u.id}`}>
                  {u.role === "admin" ? <><ShieldCheck size={11} /> Admin</> : "Consultant"}</span>
                {u.is_owner && <span className="badge-draft">Owner</span>}
                <span className={`${u.active ? "badge-approved" : "badge-nosend"}`}>{u.active ? "Active" : "Inactive"}</span>
              </div>
              <label className="flex items-center gap-2 text-sm" data-testid={`is-consultant-row-${u.id}`}>
                <input type="checkbox" checked={!!u.is_consultant} onChange={() => toggleConsultant(u)}
                  data-testid={`is-consultant-toggle-${u.id}`} />
                <span className="font-bold text-navy">is_consultant</span>
                <span className="text-xs text-[#78716C]">eligible for school assignments (does not change system role or permissions)</span>
              </label>
              {assignable && (
                <div className="space-y-2" data-testid={`assignments-block-${u.id}`}>
                  <div className="text-xs font-bold uppercase tracking-wide text-[#78716C]">Assigned schools (CKingEducation directory)</div>
                  {dirAssigns.length === 0 && <div className="text-xs text-[#78716C]">No schools assigned.</div>}
                  {dirAssigns.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 text-sm" data-testid={`assignment-chip-${a.id}`}>
                      <span className="badge-neutral">{schoolName(a.directory_school_id)}</span>
                      <button className="btn-ghost !py-0.5 !px-1.5 text-xs text-[#991B1B]" onClick={() => endAssignment(a)}
                        data-testid={`unassign-${a.id}`}><X size={12} /> Remove</button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <select className="input !py-1.5 text-xs flex-1 max-w-md" value={pick[u.id] || ""}
                      onChange={(e) => setPick({ ...pick, [u.id]: e.target.value })}
                      data-testid={`assign-school-select-${u.id}`}>
                      <option value="">Select a directory school…</option>
                      {dirSchools.filter((s) => !dirAssigns.some((a) => a.directory_school_id === s.dir_id)).map((s) => (
                        <option key={s.dir_id} value={s.dir_id}>{s.name}{s.district_name ? ` — ${s.district_name}` : ""}</option>))}
                    </select>
                    <button className="btn-outline !py-1.5 text-xs" onClick={() => assign(u)} disabled={!pick[u.id]}
                      data-testid={`assign-school-button-${u.id}`}><UserPlus size={13} /> Assign</button>
                  </div>
                </div>
              )}
              {!assignable && <div className="text-xs text-[#78716C]">Not consultant-eligible — enable is_consultant to assign schools.</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
